import { push } from 'connected-react-router';
import { CommandBar } from 'office-ui-fabric-react/lib/CommandBar';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath } from 'react-router-dom';
import { ConfirmationPrompt } from '../../../components/ConfirmationPrompt';
import {
  HEARINGROLE_INVITEE,
  HEARINGROLE_OWNER,
  HEARINGROLE_RESPONDER,
  HEARINGROLE_REVIEWER,
  HEARINGSTATUS_ACTIVE,
  HEARINGSTATUS_AWAITING_CONCLUSION,
  HEARINGSTATUS_CONCLUDED,
  HEARINGSTATUS_CREATED,
  HEARINGSTATUS_DRAFT,
} from '../../../constants/api';
import { Pages } from '../../../constants/pages';
import {
  HEARING_DETAILS_URL,
  MY_HEARINGS_ANSWERED_URL,
  MY_HEARINGS_INVITED_URL,
  MY_HEARINGS_REVIEWER_URL,
  MY_HEARINGS_WROTE_URL,
} from '../../../constants/urls';
import {
  allHearingsWithCurrentUserInRole,
  currentUserOwnsHearingByIdSelector,
} from '../../../features/apiFeatures/hearingSlice';
import { hearingStatusByHearingIdSelector } from '../../../features/apiFeatures/hearingStatusSlice';
import { currentUserIsHearingCreatorSelector } from '../../../features/authenticationSlice';
import { themeSelector } from '../../../features/bootstrapSlice';
import { getAttr } from '../../../features/helpers/apiHelpers';
import {
  createNewHearing,
  deleteHearing,
  downloadContentAsZip,
  downloadReport,
  isCreatingHearingSelector,
  isDeletingHearingSelector,
  openHearing,
} from '../../../features/pageFeatures/myHearingsPageSlice';
import { pathnameSelector } from '../../../features/routerSlice';
import { copyExternalHearingLinkToClipboard } from '../../../utilities/clipboard';
import { HearingOverview } from './HearingOverview';
import { getCommandBarItems } from './myHearingsPage/getCommandBarItems';

const styles = {
  root: {
    backgroundColor: '#FAFAFA',
    paddingRight: '0px',
  },
};

const MyHearingsPage = () => {
  const { t: translator } = useTranslation();

  const [selectedHearingId, setSelectedHearingId] = useState(null);
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);

  const dispatch = useDispatch();

  const theme = useSelector(themeSelector);

  const isHearingCreator = useSelector(currentUserIsHearingCreatorSelector);
  const hearingsWrittenSelector = allHearingsWithCurrentUserInRole(
    HEARINGROLE_OWNER,
  );
  const hearingsRepondedSelector = allHearingsWithCurrentUserInRole(
    HEARINGROLE_RESPONDER,
  );
  const hearingsReviewedSelector = allHearingsWithCurrentUserInRole(
    HEARINGROLE_REVIEWER,
  );
  const hearingsInvitedSelector = allHearingsWithCurrentUserInRole(
    HEARINGROLE_INVITEE,
  );

  const isCreatingHearing = useSelector(isCreatingHearingSelector);
  const selectedHearingStatus = useSelector(
    hearingStatusByHearingIdSelector(selectedHearingId),
  );
  const currentStatus = getAttr(selectedHearingStatus, 'status');

  const currentUserOwnsSelectedHearing = useSelector(
    currentUserOwnsHearingByIdSelector(selectedHearingId),
  );
  const isDeletingHearing = useSelector(isDeletingHearingSelector);
  const canDeleteHearing =
    !isDeletingHearing &&
    currentUserOwnsSelectedHearing &&
    (currentStatus === HEARINGSTATUS_CREATED ||
      currentStatus === HEARINGSTATUS_DRAFT);

  const isHearingConcluded = currentStatus === HEARINGSTATUS_CONCLUDED;

  const canEditHearing =
    !!selectedHearingId &&
    !isDeletingHearing &&
    !isCreatingHearing &&
    currentUserOwnsSelectedHearing &&
    !isHearingConcluded;

  const isHearingInCreatedStatus = currentStatus === HEARINGSTATUS_CREATED;

  const canDownloadReport =
    currentUserOwnsSelectedHearing &&
    (currentStatus === HEARINGSTATUS_ACTIVE ||
      currentStatus === HEARINGSTATUS_AWAITING_CONCLUSION ||
      currentStatus === HEARINGSTATUS_CONCLUDED);

  const canDownloadUserReport =
    currentUserOwnsSelectedHearing && currentStatus !== HEARINGSTATUS_CREATED;

  const canDownloadFullReport = currentUserOwnsSelectedHearing;
  const canDownloadHearingResponseReport = currentUserOwnsSelectedHearing;

  const createHearingCallback = () => dispatch(createNewHearing());
  const deleteHearingCallback = () => {
    selectedHearingId && dispatch(deleteHearing(selectedHearingId));
    setIsDeletePopupOpen(false);
  };

  const rerouteToSelectedHearing = (step) => {
    dispatch(openHearing({ hearingId: selectedHearingId, stepNumber: step }));
  };

  const downloadReportCallback = (format) =>
    dispatch(downloadReport({ hearingId: selectedHearingId, format }));

  const downloadZipCallback = () =>
    dispatch(downloadContentAsZip({ hearingId: selectedHearingId }));

  const [didCopyHearingLink, setDidCopyHearingLink] = useState(false);
  const [didCopyHearingLinkTimer, setDidCopyHearingLinkTimer] = useState(null);

  const copyHearingLink = () => {
    if (selectedHearingId) {
      copyExternalHearingLinkToClipboard(selectedHearingId);
      setDidCopyHearingLink(true);

      if (didCopyHearingLinkTimer) {
        clearTimeout(didCopyHearingLinkTimer);
      }
      setDidCopyHearingLinkTimer(
        setTimeout(() => setDidCopyHearingLink(false), 3000),
      );
    }
  };
  const canCopyHearingLink = !!selectedHearingId;

  const commandBarItems = getCommandBarItems(
    theme,
    translator,
    createHearingCallback,
    rerouteToSelectedHearing,
    isCreatingHearing,
    canDeleteHearing,
    isHearingInCreatedStatus,
    () => setIsDeletePopupOpen(true),
    canDownloadReport,
    downloadReportCallback,
    downloadZipCallback,
    canEditHearing,
    canDownloadFullReport,
    copyHearingLink,
    canCopyHearingLink,
    canDownloadUserReport,
    canDownloadHearingResponseReport,
    didCopyHearingLink,
  );
  const pathName = useSelector(pathnameSelector);

  let hearingsSelector = hearingsWrittenSelector;
  let headerText = '';
  let subHeadingText = '';
  let activePage = '';

  if (pathName === MY_HEARINGS_WROTE_URL) {
    hearingsSelector = hearingsWrittenSelector;
    headerText = translator('pageNames.myHearingsWritten');
    activePage = Pages.MyHearingsPageWritten;
  } else if (pathName === MY_HEARINGS_ANSWERED_URL) {
    hearingsSelector = hearingsRepondedSelector;
    headerText = translator('pageNames.myHearingsResponded');
    subHeadingText = translator('pages.myHearings.hearingsWrittenSubheading');
    activePage = Pages.MyHearingsPageResponded;
  } else if (pathName === MY_HEARINGS_REVIEWER_URL) {
    hearingsSelector = hearingsReviewedSelector;
    headerText = translator('pageNames.myHearingsReviewer');
    activePage = Pages.MyHearingsPageReviewed;
  } else if (pathName === MY_HEARINGS_INVITED_URL) {
    hearingsSelector = hearingsInvitedSelector;
    headerText = translator('pageNames.myHearingsInvited');
    activePage = Pages.MyHearingsPageInvited;
  }

  const onHearingClick = (hearingItem) =>
    dispatch(push(generatePath(HEARING_DETAILS_URL, { id: hearingItem.key })));

  const onHearingSelect = (hearingId) => {
    setSelectedHearingId(hearingId);
  };

  return (
    <div className="admin-page">
      {isHearingCreator ? (
        <div className="admin-page-commandBar">
          <CommandBar items={commandBarItems} styles={styles} />
        </div>
      ) : null}
      <ConfirmationPrompt
        isOpen={isDeletePopupOpen}
        onDismiss={() => setIsDeletePopupOpen(false)}
        onConfirm={deleteHearingCallback}
        text={translator('pages.myHearings.deleteHearingPrompt')}
      />
      <HearingOverview
        headerText={headerText}
        subHeadingText={subHeadingText}
        hearingsSelector={hearingsSelector}
        disableOwner={true}
        onHearingClick={onHearingClick}
        onHearingSelect={onHearingSelect}
        disableSelection={
          pathName === MY_HEARINGS_ANSWERED_URL ||
          pathName === MY_HEARINGS_INVITED_URL
        }
        activePage={activePage}
      />
    </div>
  );
};

export { MyHearingsPage };
